"use client"

import { useRouter } from "next/navigation"

import { Button } from "@/components/ui/button"
import { CasinoIcon } from "@/src/shared/assets/icons"

const flatIcon = "[&_path]:fill-current [&_path]:[fill-opacity:1]"

type CardProps = {
  title: string
  description: string
  label: string
  time: string
  unread: boolean
  expanded: boolean
  actionUrl: string | null
  actionLabel: string | null
  onToggle: () => void
}

export function Card({
  title,
  description,
  label,
  time,
  unread,
  expanded,
  actionUrl,
  actionLabel,
  onToggle
}: CardProps) {
  const router = useRouter()
  const showAction = expanded && actionUrl !== null

  return (
    <article
      className={`rounded-[10px] border bg-black-800 ${expanded ? "border-purple-200" : "border-white/10"}`}
      role="listitem"
    >
      <button
        type="button"
        className="flex w-full cursor-pointer gap-3 p-3 text-left"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <CasinoIcon
          aria-hidden
          className={`size-11 shrink-0 ${flatIcon} ${unread ? "text-purple-300" : "text-white/50"}`}
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-start justify-between gap-2">
            <span
              className={
                unread ? "text-sm text-purple-300" : "text-sm text-white/50"
              }
            >
              {label}
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-white/50">
              <time>{time}</time>
              {unread ? (
                <span
                  aria-hidden
                  className="size-2 rounded-full bg-purple-300"
                />
              ) : null}
            </span>
          </span>
          <span className="mt-1 block text-white">{title}</span>
          <span
            className={`mt-1 block text-sm ${unread ? "text-white/75" : "text-white/25"}`}
          >
            {description}
          </span>
        </span>
      </button>
      {showAction && actionUrl !== null ? (
        <div className="px-2 pb-3">
          <Button
            type="button"
            variant="hand"
            className="w-full"
            onClick={(event) => {
              event.stopPropagation()
              if (!actionUrl) return
              router.push(actionUrl)
            }}
          >
            {actionLabel}
          </Button>
        </div>
      ) : null}
    </article>
  )
}
